import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { statusClass } from "@/lib/status";
import { fmtDate } from "@/lib/dates";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EffectivenessTab from "@/components/EffectivenessTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDateTime, joinMeta, plural, hhmm } from "@/lib/utils";
import { useAuth, canWrite } from "@/context/AuthContext";
import { ArrowLeft, Printer, Download, Award, Edit3, XCircle, Calendar, Upload, Trash2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSearch } from "@/lib/search";
import { FieldLabel } from "@/components/FieldLabel";
import { DetailSkeleton } from "@/components/Skeletons";

const PARTICIPANT_FIELDS = ["full_name", "employee_code", "designation", "department_name"];
// A stable empty array: `x || []` is a fresh reference every render and defeats the memo.
const NO_PARTICIPANTS = [];

const badge = statusClass;
const TABS = ["overview", "participants", "attendance", "effectiveness", "files", "timeline"];

const CANCEL = {
  action: "cancel", destructive: true, confirm: "Cancel training",
  title: "Cancel this training?",
  blurb: "It stays in the register, struck through on the calendar, so the slot it occupied is still visible. Attendance can no longer be marked against it.",
  placeholder: "Why it is not going ahead",
};

export default function TrainingDetail() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [attendance, setAttendance] = useState({});
  // Which rows somebody has actually touched since the last save. Everything else in
  // `attendance` is a copy of what the server sent, and posting that whole map back was
  // how people nobody had marked ended up carrying a marked_by and a marked_at.
  const [touched, setTouched] = useState(() => new Set());
  // Controlled, so leaving the attendance tab can be held while there is unsaved work.
  // `?tab=` lets another screen land here on a tab — the panel beside the register
  // offers "Attendance" and should open on it, not on the overview.
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(TABS.includes(sp.get("tab")) ? sp.get("tab") : "overview");
  const [leaving, setLeaving] = useState(null);
  // Cancelling is the one action that owes an explanation, so it keeps the dialog.
  const [asking, setAsking] = useState(null);
  const [lateOpen, setLateOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reason, setReason] = useState("");
  const [uploading, setUploading] = useState(false);
  const [attQ, setAttQ] = useState("");
  // The signed physical sheet. `sheetOpen` is "upload" or "replace" while the dialog is
  // up; a replacement carries a reason that is kept with the superseded scan.
  const [sheetOpen, setSheetOpen] = useState(null);
  const [sheetFile, setSheetFile] = useState(null);
  const [sheetReason, setSheetReason] = useState("");
  const [sheetError, setSheetError] = useState("");
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sheetPrompted, setSheetPrompted] = useState(false);
  // A change the server accepted whose reload then failed. Held until a load succeeds.
  const [stale, setStale] = useState(false);

  // Numbered, because an action's reload is not awaited and two can be out at once, and
  // whichever answered last decided the screen: a slow reply could put an older training
  // back, and a slow failure could call a screen that had just reloaded out of date.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const r = await api.get(`/trainings/${id}`);
      if (seq !== loadSeq.current) return;
      setT(r.data);
      const map = {};
      (r.data.participants || []).forEach((p) => { map[p.employee_id] = { status: p.attendance_status, remarks: p.remarks || "" }; });
      setAttendance(map);
      setTouched(new Set());
      setStale(false);
    } catch (e) {
      if (seq === loadSeq.current) throw e;
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // After a change the server has already accepted and announced. The reload used to fail
  // in silence, so the status badge, the sheet and the certificate column went on showing
  // the training as it was before the action the toast had just confirmed.
  const refresh = async () => {
    try {
      await load();
    } catch {
      setStale(true);
      toast.error("Saved, but the training could not be refreshed — reload the page");
    }
  };

  const dirty = touched.size;

  // The register is a hundred radio buttons that live in this component until Save is
  // pressed. Closing the tab or reloading took the lot with nothing said — and on a long
  // list the Save button had long since scrolled off the top of the card.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // beforeunload covers closing the tab and reloading; the tab strip below covers moving
  // to Overview or Timeline. Neither sees React Router navigating — so clicking
  // "Trainings" in the sidebar, or the browser's back button, dropped the whole register
  // without a word. That is the likeliest way to lose a hundred marks, and it was the one
  // exit with no guard on it.
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirty > 0 && currentLocation.pathname !== nextLocation.pathname);

  // One dialog, two kinds of leaving: a tab within this screen, and a navigation away
  // from it. `held` is how each of them is let through or called back, so the dialog
  // itself does not know which one it is answering.
  const held = leaving !== null
    ? { proceed: () => { setLeaving(null); setTab(leaving); }, stay: () => setLeaving(null) }
    : blocker.state === "blocked"
      ? { proceed: () => blocker.proceed(), stay: () => blocker.reset() }
      : null;

  // Above the early return: hooks cannot run conditionally.
  const participants = t?.participants || NO_PARTICIPANTS;
  const shownParticipants = useSearch(participants, attQ, PARTICIPANT_FIELDS);
  const total = participants.length;
  const marked = participants.filter(
    (p) => (attendance[p.employee_id]?.status || "Not Marked") !== "Not Marked").length;

  if (!t) return <DetailSkeleton testid="training-detail-skeleton" />;

  // Returns whether it worked, so a dialog can stay open when it did not.
  const doAction = async (action, comments = "") => {
    try {
      await api.post(`/trainings/${id}/workflow`, { action, comments });
      toast.success("Updated");
      refresh();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  // Acts on whoever is on screen. With a search running that is the filtered few, which is
  // the whole reason somebody searched; with the box empty it is everyone, as before. The
  // button says which, so it can never be a surprise.
  const markShown = (status) => () => {
    const m = { ...attendance };
    const marked = new Set(touched);
    shownParticipants.forEach((p) => {
      m[p.employee_id] = { status, remarks: m[p.employee_id]?.remarks || "" };
      marked.add(p.employee_id);
    });
    setAttendance(m);
    setTouched(marked);
  };

  const setMark = (employee_id, status) => {
    setAttendance((s) => ({ ...s, [employee_id]: { ...s[employee_id], status } }));
    setTouched((s) => new Set(s).add(employee_id));
  };

  // Back to exactly what the server holds. Clearing `touched` alone would leave the
  // discarded marks showing on the radios, which reads as saved.
  const discardMarks = () => {
    const map = {};
    (t.participants || []).forEach((p) => { map[p.employee_id] = { status: p.attendance_status, remarks: p.remarks || "" }; });
    setAttendance(map);
    setTouched(new Set());
  };

  // Only what was touched. Sending the whole map meant every untouched person was
  // written back as "Not Marked" *by this user, at this moment* — a hundred rows
  // claiming somebody had been through them.
  const saveAttendance = async () => {
    const marks = [...touched].map((employee_id) => ({
      employee_id,
      attendance_status: attendance[employee_id]?.status || "Not Marked",
      remarks: attendance[employee_id]?.remarks || "",
    }));
    if (!marks.length) return true;
    // Refused outright once certificates are out — marking that silently failed read
    // as marking that saved.
    try {
      const r = await api.post(`/trainings/${id}/attendance`, { marks });
      toast.success(r.data?.auto_completed
        ? "Attendance saved — every name is marked, so the training is now Completed"
        : `Attendance saved · ${r.data?.marked ?? 0} of ${r.data?.total ?? 0} marked`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      // Says whether the register actually took the marks. It used to swallow this and
      // resolve, so "Save and go" went — throwing away the very marks it had offered to
      // keep, with a red toast behind a screen the user had already left.
      return false;
    }
    // The register has them from here, whatever the reload does. The reload used to share
    // the POST's try, so a failed GET after an accepted save kept the marks counted as
    // unsaved: Save offered to post them again, "Save and go" refused to leave, and Discard
    // repainted people as Not Marked who were marked on the server.
    setTouched(new Set());
    await refresh();
    // The marks came off a sheet people signed. Asked for once per visit, right after
    // the save, while the paper is still on the desk — not chased later.
    if (!t.attendance_sheet && !sheetPrompted) { setSheetPrompted(true); openSheet("upload"); }
    return true;
  };

  const openSheet = (mode) => { setSheetFile(null); setSheetReason(""); setSheetError(""); setSheetOpen(mode); };

  const submitSheet = async () => {
    if (!sheetFile) { setSheetError("Choose the scan first."); return; }
    if (sheetOpen === "replace" && !sheetReason.trim()) { setSheetError("Say why the sheet on file is being replaced."); return; }
    setSheetBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", sheetFile);
      if (sheetOpen === "replace") fd.append("reason", sheetReason.trim());
      await api.post(`/trainings/${id}/attendance-sheet`, fd);
      toast.success(sheetOpen === "replace" ? "Signed sheet replaced — the previous scan is kept as superseded" : "Signed sheet on file");
      setSheetOpen(null);
      refresh();
    } catch (e) {
      const fields = e.response?.data?.fields || {};
      setSheetError(fields.file || fields.reason || formatApiError(e.response?.data?.detail, "Upload failed"));
    }
    setSheetBusy(false);
  };

  // Somebody marked Late attended. Whether that earns a certificate is HR's call about
  // those particular people, so it is asked here rather than decided by a query — and
  // only when there is actually somebody to ask about.
  const uncertified = (t) => (t?.participants || []).filter((p) => !p.certificate_number);
  const lateWaiting = uncertified(t).filter((p) => p.attendance_status === "Late");
  const presentWaiting = uncertified(t).filter((p) => p.attendance_status === "Present");

  const issue = async (includeLate) => {
    setLateOpen(false);
    try {
      const r = await api.post(`/trainings/${id}/certificates/generate`,
                               { include_late: !!includeLate });
      toast.success(`${plural(r.data.issued, "certificate")} issued`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // No sheet, no certificates: the server refuses too, but the person is taken to the
  // place the sheet is uploaded rather than left with a toast.
  const generateCerts = () => {
    if (!t.attendance_sheet) {
      toast.error("Upload the signed attendance sheet first — certificates rest on it.");
      setTab("attendance");
      openSheet("upload");
      return;
    }
    return lateWaiting.length ? setLateOpen(true) : issue(false);
  };

  const removeFile = async () => {
    try {
      await api.delete(`/attachments/${removing.id}`);
      toast.success(`"${removing.file_name}" removed`);
      setRemoving(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not remove the file")); }
  };

  // What kind of file this is used to be worked out here and sent as a query parameter,
  // by a helper that does not exist in this project — so every upload threw before it
  // reached the network and the catch below reported "Upload failed" with nothing behind
  // it. The server derives the kind from the file's own content type now; the browser is
  // not asked, and would not be believed.
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    // The input keeps its value, so picking the same file twice in a row is otherwise
    // ignored — including straight after a failure, which is exactly when it is retried.
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const failed = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/trainings/${id}/attachments`, fd);
      } catch (err) {
        // Named, and one at a time: uploading five photos and being told "Upload failed"
        // does not say which of the five to try again.
        failed.push(`${file.name}: ${formatApiError(err.response?.data?.detail, "upload failed")}`);
      }
    }
    const uploaded = files.length - failed.length;
    if (uploaded) toast.success(`${plural(uploaded, "file")} uploaded`);
    failed.forEach((m) => toast.error(m));
    // Nothing to catch up on when every file was refused. A reload that threw here also
    // left the drop zone reading "Uploading..." for good.
    if (uploaded) await refresh();
    setUploading(false);
  };

  const present = (t.participants || []).filter((p) => p.attendance_status === "Present").length;

  return (
    <div className="space-y-5" data-testid="training-detail">
      <Button variant="ghost" onClick={() => nav("/trainings")} className="pl-0"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-primary">{t.category} · {t.training_type}</div>
          <h2 className="font-heading font-bold text-3xl mt-1">{t.title}</h2>
          <div className="text-sm text-muted-foreground mt-1">{joinMeta(fmtDate(t.training_date), t.venue, (t.unit_names || []).join(", ") || t.unit_name, t.trainer_name && `Trainer: ${t.trainer_name}`)}</div>
          {(t.unit_names || []).length > 1 && (
            <div className="text-xs text-muted-foreground mt-1" data-testid="training-primary-unit">
              Certificates for this training name {t.unit_name}.
            </div>
          )}
          {(t.department_names || []).length > 0 && (
            <div className="text-xs text-muted-foreground mt-0.5" data-testid="training-departments">
              Departments: {t.department_names.join(", ")}
            </div>
          )}
          <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${badge(t.workflow_status)}`} data-testid="training-status">{t.workflow_status}</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {writable && t.workflow_status === "Draft" && (
            <>
              <Button variant="outline" onClick={() => nav(`/trainings/${id}/edit`)}><Edit3 className="w-4 h-4 mr-1.5" /> Edit</Button>
              <Button onClick={() => doAction("schedule")} data-testid="schedule-btn"><Calendar className="w-4 h-4 mr-1.5" /> Schedule</Button>
            </>
          )}
          {writable && t.workflow_status === "Scheduled" && (
            <>
              {/* Editing was Draft-only because approval meant approved content should not
                  change underneath it. There is no approval now, and what a certificate
                  states is frozen by the certificate itself, not by the status — nothing is
                  issued at Scheduled. Nominating somebody onto a confirmed training needed
                  the /edit URL typed by hand until this. */}
              <Button variant="outline" onClick={() => nav(`/trainings/${id}/edit`)} data-testid="edit-scheduled-btn"><Edit3 className="w-4 h-4 mr-1.5" /> Edit</Button>
              <Button variant="outline" onClick={() => doAction("reopen")} data-testid="reopen-btn">Back to Draft</Button>
              <Button variant="destructive" onClick={() => setAsking(CANCEL)} data-testid="cancel-btn"><XCircle className="w-4 h-4 mr-1.5" /> Cancel</Button>
            </>
          )}
          {["Scheduled", "Completed"].includes(t.workflow_status) && (
            <Button variant="outline" onClick={() => window.open(`${API}/trainings/${id}/attendance-sheet.pdf`)} data-testid="print-attendance"><Printer className="w-4 h-4 mr-1.5" /> Attendance Sheet</Button>
          )}
          {t.workflow_status === "Completed" && (
            <>
              {/* Attendance is what completes a training, and attendance gets marked
                  wrong. Completed used to be a one-way door, so somebody put down as
                  Absent who was in the room had no remedy. This steps back to Scheduled,
                  never to Draft — the training did happen — and the API refuses while any
                  certificate issued for it still stands. */}
              {writable && <Button variant="outline" onClick={() => doAction("reopen")} data-testid="reopen-completed-btn">Reopen</Button>}
              {writable && <Button onClick={generateCerts} data-testid="gen-certs"><Award className="w-4 h-4 mr-1.5" /> Issue Certificates</Button>}
              {/* The bundle route answers 404 when nothing has been issued, so offering
                  it on a training with no certificates was a button that could only fail. */}
              {(t.participants || []).some((p) => p.certificate_number) && (
                <Button variant="outline" onClick={() => window.open(`${API}/trainings/${id}/certificates/bundle`)} data-testid="bundle-certs"><Download className="w-4 h-4 mr-1.5" /> Download all certificates</Button>
              )}
            </>
          )}
        </div>
      </div>

      {stale && (
        // Until a reload succeeds, not for as long as a toast. With the toast gone the badge,
        // the buttons that follow it, the marks and the certificate column all read as current.
        <div role="alert" data-testid="training-stale"
             className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium text-destructive-text">Could not reload this training</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Your last change went through. What is shown here is the training as it was before it, so
              the status, marks, signed sheet and certificates may be out of date.
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="training-stale-retry">Try again</Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => {
        if (dirty && tab === "attendance") setLeaving(v); else setTab(v);
      }}>
        {/* The strip scrolls; the PAGE must not. Six triggers come to 580px and TabsList
            is an inline-flex of whitespace-nowrap buttons, so at 390px it pushed the
            whole document sideways — the title, the back link and the sidebar all
            clipped, on every tab. That was already true of five triggers before
            Effectiveness was added; adding one made a pre-existing overflow worse and a
            sweep at phone width is what finally measured it. */}
        <div className="overflow-x-auto -mx-1 px-1">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">Participants ({(t.participants || []).length})</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="effectiveness" data-testid="tab-effectiveness">Effectiveness</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Files ({(t.attachments || []).length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card className="p-6 border border-border">
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div><div className="overline text-muted-foreground">Description</div><div className="mt-1">{t.description || "—"}</div></div>
              <div><div className="overline text-muted-foreground">Duration</div><div className="mt-1">{joinMeta(`${t.duration_hours} hrs`, t.start_time && t.end_time && `${hhmm(t.start_time)}–${hhmm(t.end_time)}`)}</div></div>
              <div><div className="overline text-muted-foreground">Mode</div><div className="mt-1">{t.mode}</div></div>
              <div><div className="overline text-muted-foreground">Estimated cost</div><div className="mt-1 font-mono">₹{Number(t.estimated_cost || 0).toLocaleString('en-IN')}</div></div>
              <div><div className="overline text-muted-foreground">Actual cost</div><div className="mt-1 font-mono">₹{Number(t.actual_cost || 0).toLocaleString('en-IN')}</div></div>
              <div><div className="overline text-muted-foreground">Attendance</div><div className="mt-1">{present} / {(t.participants||[]).length} present</div></div>
            </div>
            {t.rejection_reason && (
              <div className="mt-4 p-3 rounded bg-rose-500/10 text-rose-700 dark:text-rose-400 text-sm">
                <b>Reason:</b> {t.rejection_reason}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="participants" className="mt-4">
          <Card className="border border-border overflow-hidden">
            {/* Scrolls inside the card, as the other registers do. Without it the card's
                overflow-hidden cut the table off at Department on a phone, and Status and
                the certificate number sat past an edge nothing could scroll to. */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-zebra">
                <thead className="bg-muted/70 border-b border-border"><tr className="text-left">
                  <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th><th className="px-4 py-3">Department</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Certificate</th>
                </tr></thead>
                <tbody>
                  {(t.participants || []).map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0" data-testid={`participant-${p.employee_code}`}>
                      <td className="px-4 py-2 font-mono">{p.employee_code}</td>
                      <td className="px-4 py-2 font-medium">{p.full_name}</td>
                      <td className="px-4 py-2">{p.designation}</td>
                      <td className="px-4 py-2">{p.department_name}</td>
                      <td className="px-4 py-2"><Badge variant="outline">{p.attendance_status}</Badge></td>
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">
                        {p.certificate_number ? (
                          <a href={`${API}/certificates/${p.id}/pdf`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{p.certificate_number}</a>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <Card className="p-6 border border-border">
            <div className="flex justify-between mb-4 flex-wrap gap-3">
              <div>
                <div className="overline text-muted-foreground">Mark attendance</div>
                {/* A 106-person list with no counter gave no way to tell how far you had
                    got, and no way to find one person to correct. */}
                <div className="text-sm text-muted-foreground mt-1" data-testid="attendance-progress">
                  {marked} of {total} marked
                  {attQ ? ` · showing ${shownParticipants.length}` : ""}
                  {dirty > 0 && (
                    <span className="text-destructive-text"> · {plural(dirty, "unsaved change")}</span>
                  )}
                </div>
              </div>
              {/* Wraps. Three buttons come to 356px and at 390px, with the page padding,
                  they pushed the document 7px sideways — enough to clip the left edge of
                  every heading on the tab. Pre-existing; measured by the phone-width
                  pass of the effectiveness sweep. */}
              <div className="flex flex-wrap gap-2">
                {writable && (
                  <>
                    <Button variant="outline" onClick={markShown("Present")} data-testid="mark-all-present">
                      {attQ ? `Mark ${shownParticipants.length} shown present` : "Mark all present"}
                    </Button>
                    <Button variant="outline" onClick={markShown("Absent")} data-testid="mark-all-absent">
                      {attQ ? `Mark ${shownParticipants.length} shown absent` : "Mark all absent"}
                    </Button>
                  </>
                )}
                {/* Says how much is riding on it, and nothing to press when there is
                    nothing to send. A Save that always looked identical gave no way to
                    tell work that had reached the register from work that had not. */}
                {writable && (
                  <Button onClick={saveAttendance} disabled={!dirty} data-testid="save-attendance">
                    {dirty ? `Save ${plural(dirty, "change")}` : "Saved"}
                  </Button>
                )}
              </div>
            </div>
            {/* The paper behind the marks. Present or missing, it is said here, above the
                register it evidences, and never quietly. */}
            {["Scheduled", "Completed"].includes(t.workflow_status) && (
              t.attendance_sheet ? (
                <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2.5 text-sm flex items-start justify-between gap-3 flex-wrap"
                     data-testid="sheet-on-file">
                  <div className="min-w-0">
                    <div className="font-medium text-emerald-700 dark:text-emerald-400">Signed sheet on file</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {joinMeta(t.attendance_sheet.file_name, `${Math.round((t.attendance_sheet.file_size || 0) / 1024)} KB`,
                                t.attendance_sheet.uploaded_by_name && `uploaded by ${t.attendance_sheet.uploaded_by_name}`,
                                formatDateTime(t.attendance_sheet.uploaded_at))}
                    </div>
                    {(t.attendance_sheet_history || []).length > 0 && (
                      <details className="mt-1 text-xs text-muted-foreground" data-testid="sheet-history">
                        <summary className="cursor-pointer">{plural(t.attendance_sheet_history.length, "earlier scan")} superseded</summary>
                        <ul className="mt-1 space-y-0.5 pl-3 list-disc">
                          {t.attendance_sheet_history.map((h) => (
                            <li key={h.id}>
                              <a href={`${API}/attachments/${h.id}/download?auth=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" className="hover:underline">{h.file_name}</a>
                              {" — "}{h.note || "no reason recorded"}{" · "}{formatDateTime(h.uploaded_at)}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" data-testid="sheet-view"
                            onClick={() => window.open(`${API}/attachments/${t.attendance_sheet.id}/download?auth=${localStorage.getItem("token")}`)}>
                      View scan
                    </Button>
                    {writable && <Button variant="ghost" size="sm" onClick={() => openSheet("replace")} data-testid="sheet-replace">Replace…</Button>}
                  </div>
                </div>
              ) : (
                <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-sm flex items-start justify-between gap-3 flex-wrap"
                     data-testid="sheet-missing">
                  <div>
                    <div className="font-medium text-amber-700 dark:text-amber-400">No signed sheet on file</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Scan the sheet people signed in the room and upload it here. Certificates cannot be issued until it is on file.
                    </div>
                  </div>
                  {writable && (
                    <Button size="sm" onClick={() => openSheet("upload")} data-testid="sheet-upload">
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload signed sheet
                    </Button>
                  )}
                </div>
              )
            )}
            <div className="mb-3">
              <SearchInput value={attQ} onChange={setAttQ} testid="attendance-search"
                           placeholder="Find a person by name, code, designation or department..." />
            </div>
            <div className="divide-y divide-border max-h-[500px] overflow-y-auto overflow-x-hidden pr-1">
              {shownParticipants.length === 0 && (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  Nobody here matches "{attQ}".
                </div>
              )}
              {shownParticipants.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-3 gap-3">
                  {/* min-w-0 so this column can shrink: a flex item defaults to
                      min-width:auto, which kept the radios from fitting and pushed
                      "Late" out past the edge behind a horizontal scrollbar. */}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.full_name} <span className="text-xs text-muted-foreground font-mono">{p.employee_code}</span></div>
                    <div className="text-xs text-muted-foreground truncate">{p.designation} · {p.department_name}</div>
                  </div>
                  <RadioGroup value={attendance[p.employee_id]?.status || "Not Marked"} onValueChange={(v) => setMark(p.employee_id, v)} className="flex gap-3 shrink-0" data-testid={`att-radio-${p.employee_code}`}>
                    {["Present", "Absent", "Late"].map((v) => (
                      <label key={v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <RadioGroupItem value={v} /> {v}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
            {/* Sticks to the bottom of the card, because on a 106-name list the Save
                button at the top is several screens away by the time you have finished
                marking and the work is still sitting in the browser. */}
            {writable && dirty > 0 && (
              <div className="sticky bottom-0 -mx-6 -mb-6 mt-3 px-6 py-3 border-t border-border bg-card/95 backdrop-blur flex items-center justify-between gap-3 flex-wrap"
                   data-testid="attendance-unsaved-bar">
                <span className="text-sm">{plural(dirty, "mark")} not saved yet.</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={discardMarks} data-testid="attendance-discard">Discard</Button>
                  <Button size="sm" onClick={saveAttendance} data-testid="attendance-save-sticky">Save</Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="effectiveness" className="mt-4">
          {/* A sibling of Attendance, not part of it: attendance is recorded on the day
              and this weeks later, by whoever watched the person back on the job. */}
          <EffectivenessTab trainingId={id} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="files" className="mt-4 space-y-3">
          <Card className="p-6 border border-border">
            {writable && <Label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-border rounded-md p-8 hover:bg-muted/50 justify-center" data-testid="file-upload-label">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Click to upload photos or presentations"}
              <input type="file" className="hidden" multiple onChange={upload} data-testid="file-input" />
            </Label>}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              {(t.attachments || []).map((a) => (
                <div key={a.id} className="border border-border rounded-md p-3 text-sm flex items-start justify-between gap-2 hover:bg-muted/50">
                  <a href={`${API}/attachments/${a.id}/download?auth=${localStorage.getItem("token")}`}
                     target="_blank" rel="noreferrer" className="min-w-0 flex-1" data-testid={`file-${a.id}`}>
                    <div className="font-medium truncate">{a.file_name}</div>
                    <div className="text-xs text-muted-foreground">{a.file_type} · {Math.round((a.file_size||0)/1024)} KB</div>
                  </a>
                  {writable && (
                    <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7" onClick={() => setRemoving(a)}
                            aria-label={`Remove ${a.file_name}`} data-testid={`file-remove-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive-text" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card className="p-6 border border-border">
            <ol className="relative border-l border-border ml-3 space-y-4">
              {(t.approval_history || []).map((h) => (
                <li key={h.id} className="ml-6">
                  <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-primary" />
                  <div className="text-sm font-medium">{h.action}</div>
                  <div className="text-xs text-muted-foreground">{joinMeta(h.actioned_by_name, formatDateTime(h.actioned_at))}</div>
                  {h.comments && <div className="text-sm mt-1">{h.comments}</div>}
                </li>
              ))}
            </ol>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!sheetOpen} onOpenChange={(v) => !v && setSheetOpen(null)}>
        <DialogContent data-testid="sheet-dialog">
          <DialogHeader>
            <DialogTitle>{sheetOpen === "replace" ? "Replace the signed sheet" : "Upload the signed sheet"}</DialogTitle>
            <DialogDescription>
              {sheetOpen === "replace"
                ? "The scan on file is kept as superseded, with your reason beside it. Nothing is lost; the register shows which scan the marks rest on."
                : "A PDF or a photo of the sheet people signed in the room. It is kept against this training as the paper behind the marks."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <FieldLabel htmlFor="sheet-file" required>Scan</FieldLabel>
              <Input id="sheet-file" type="file" accept="application/pdf,image/*" data-testid="sheet-file"
                     onChange={(e) => { setSheetFile(e.target.files?.[0] || null); setSheetError(""); }} />
            </div>
            {sheetOpen === "replace" && (
              <div>
                <FieldLabel htmlFor="sheet-reason" required>Why it is being replaced</FieldLabel>
                <Textarea id="sheet-reason" value={sheetReason} onChange={(e) => { setSheetReason(e.target.value); setSheetError(""); }}
                          placeholder="e.g. First scan was missing page 2" data-testid="sheet-reason" />
              </div>
            )}
            {sheetError && <p role="alert" className="text-xs text-destructive-text" data-testid="sheet-error">{sheetError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSheetOpen(null)} data-testid="sheet-later">{sheetOpen === "replace" ? "Cancel" : "Later"}</Button>
            <Button onClick={submitSheet} disabled={sheetBusy} data-testid="sheet-submit">
              {sheetBusy ? "Uploading..." : sheetOpen === "replace" ? "Replace" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lateOpen} onOpenChange={setLateOpen}>
        <DialogContent data-testid="late-dialog">
          <DialogHeader>
            <DialogTitle>Some people were marked Late</DialogTitle>
            <DialogDescription>
              {presentWaiting.length > 0
                ? `${plural(presentWaiting.length, "person", "people")} marked Present will receive a certificate. `
                : "Everyone marked Present already holds one. "}
              Late arrivals still attended — including them is your call, and it applies to this
              batch only.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border p-3 text-sm space-y-1" data-testid="late-list">
            {lateWaiting.map((p) => (
              <div key={p.id} className="flex justify-between gap-3">
                <span className="truncate">{p.full_name}</span>
                <span className="text-xs text-muted-foreground font-mono shrink-0">{p.employee_code}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => issue(false)} data-testid="late-exclude">
              {presentWaiting.length > 0 ? `Present only (${presentWaiting.length})` : "Leave them out"}
            </Button>
            <Button onClick={() => issue(true)} data-testid="late-include">
              Include the {plural(lateWaiting.length, "late arrival")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Held rather than asked-before: the marks are already made, and the question is
          only whether to keep them. Discard writes the radios back to what the server
          holds, so what is on screen never disagrees with what is stored. */}
      <AlertDialog open={!!held} onOpenChange={(o) => { if (!o) held?.stay(); }}>
        <AlertDialogContent data-testid="unsaved-attendance-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{plural(dirty, "mark")} not saved</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing has reached the register yet. Leave without saving and they are gone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unsaved-stay">Stay here</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                // Held open until the register answers. If the save is refused the marks
                // are still on screen and still unsaved, which is the only honest place
                // to be — going anyway would discard them under the word "Save".
                e.preventDefault();
                if (await saveAttendance()) held.proceed();
              }}
              data-testid="unsaved-save-and-go">Save and go</AlertDialogAction>
            <AlertDialogAction
              onClick={() => { discardMarks(); held.proceed(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="unsaved-discard">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removing} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
        <AlertDialogContent data-testid="file-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{removing?.file_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops being listed against this training. The upload stays in the audit log, so a
              record of it having been here survives — but nobody will find the file from this page
              again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeFile} data-testid="file-remove-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!asking} onOpenChange={(v) => !v && setAsking(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>{asking?.title}</DialogTitle>
            <DialogDescription>{asking?.blurb}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <FieldLabel htmlFor="wf-reason" required>Reason</FieldLabel>
            <Textarea id="wf-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder={asking?.placeholder} data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsking(null)}>Cancel</Button>
            <Button variant={asking?.destructive ? "destructive" : "default"} onClick={async () => {
              // The API requires a reason. Closing regardless threw away what had been
              // typed and sent the user back to find the training again.
              if (await doAction(asking.action, reason)) { setAsking(null); setReason(""); }
            }} data-testid="reject-confirm">{asking?.confirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
